import MoreMenuItem from "@/utils/UIConstants/MoreMenuItem.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import CapabilityType from "@/utils/constants/CapabilityType.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import EditScreenLayout = namaTypes.EditScreenLayout;
import ListScreenLayout = namaTypes.ListScreenLayout;
import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;

const CREATE_PRINTING_FORM_FROM_SCREEN = "createPrintingFormFromScreen";
const PRINTING_FORM_FROM_SCREEN_URL = "printingFormFromScreen";
const PAGES_SCOPE_FIELD = "printingFormPagesScope";
const PAGES_SCOPE_ENUM = "PrintingFormPagesScope";
const ALL_PAGES_IN_ONE_FORM = "AllPagesInOneForm";
const PAGES_SCOPES = ["CurrentPageOnly", "MainPageOnly", "EveryPageInSeparateForms", ALL_PAGES_IN_ONE_FORM];

export default class PrintingFormFromScreenUtils {
  static userCanCreatePrintingForm(): boolean {
    return SecurityUtils.userCan(BSCEntities.PrintingFormWizard, CapabilityType.CreateDraft);
  }

  static createFromEditScreenMenuItem(entityType: string, layout: EditScreenLayout | undefined,
                                      currentPageIndex: number | undefined): MoreMenuItem {
    return MoreMenuItem.createMoreMenuItem(CREATE_PRINTING_FORM_FROM_SCREEN, async () => {
      await this.askForPagesScope(entityType, layout, currentPageIndex);
    });
  }

  static createFromListScreenMenuItem(entityType: string, layout: ListScreenLayout | undefined): MoreMenuItem {
    return MoreMenuItem.createMoreMenuItem(CREATE_PRINTING_FORM_FROM_SCREEN, async () => {
      await this.createPrintingForm({entityType: entityType, listScreenLayout: JSON.stringify(layout)});
    });
  }

  private static async askForPagesScope(entityType: string, layout: EditScreenLayout | undefined,
                                       currentPageIndex: number | undefined) {
    const okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (answers: Record<string, any>) => {
      if (!answers)
        return;
      await this.createPrintingForm({
        entityType: entityType,
        editScreenLayout: JSON.stringify(layout),
        pagesScope: answers[PAGES_SCOPE_FIELD] ?? ALL_PAGES_IN_ONE_FORM,
        currentPageIndex: (currentPageIndex ?? 0) + "",
      });
    });
    const cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
    });
    const questionFields: QuestionField[] = [this.createPagesScopeField()];
    const defaultValues: DTOFilledQuestionField[] = [
      {id: PAGES_SCOPE_FIELD, value: ALL_PAGES_IN_ONE_FORM, objValue: ALL_PAGES_IN_ONE_FORM}
    ];
    const questionPresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter(entityType);
    await questionPresenter.edit(questionFields, defaultValues);
    const dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateId(CREATE_PRINTING_FORM_FROM_SCREEN)!, false);
    await dialogBox.createPresenter(questionPresenter as WidgetsPresenter, questionFields, defaultValues);
    await dialogBox.runDialogBox();
  }


  private static createPagesScopeField(): QuestionField {
    const pagesScopeField: QuestionField =
      NamaCommon.createQuestionFieldWithAllowedValues(PAGES_SCOPE_FIELD, FieldType.Enum, PAGES_SCOPES, undefined, true);
    pagesScopeField.metaData!.enumTypeName = PAGES_SCOPE_ENUM;
    return pagesScopeField;
  }

  private static async createPrintingForm(data: Record<string, string>) {

    const loadingStore = useLoadingStore();
    loadingStore.startLoading();
    let createdFormLinks: string[] = [];
    try {
      const response = await NamaCommon.namaFetch(PRINTING_FORM_FROM_SCREEN_URL, {data: data});
      createdFormLinks = this.linksToCreatedForms(response?.createdIds);
    } finally {
      loadingStore.stopLoading();
    }
    this.showCreatedForms(createdFormLinks);
  }

  private static showCreatedForms(createdFormLinks: string[]) {
    if (ObjectChecker.isEmptyOrNull(createdFormLinks))
      return;
    const openedWindows = createdFormLinks.map(link => window.open(link, "_blank"));
    if (openedWindows.every(openedWindow => !openedWindow))
      window.location.href = createdFormLinks[0];
  }

  private static linksToCreatedForms(createdIds: string[] | undefined): string[] {
    if (ObjectChecker.isEmptyOrNull(createdIds))
      return [];
    return createdIds!.map(createdId => this.linkToCreatedForm(createdId))
      .filter(link => ObjectChecker.isNotEmptyOrNull(link)) as string[];
  }

  private static linkToCreatedForm(createdId: string | undefined): string | undefined {
    if (ObjectChecker.isEmptyOrNull(createdId))
      return undefined;
    const createdFormPlace = PlaceUtils.editPlace(BSCEntities.PrintingFormWizard, createdId);
    return createdFormPlace ? PlaceUtils.linkToPlace(createdFormPlace) : undefined;
  }
}
